import { z } from 'zod'

/**
 * Gewinnspiel zum Opening: Auf Instagram lief ein Gewinnspiel mit mehreren
 * Preisen (je einer von einer Firma). Am Abend lost K4ia auf dem großen
 * Bildschirm (`/gewinnspiel`) aus, gesteuert vom Handy
 * (`/gewinnspiel/go/<token>`), so wie beim Intro.
 *
 * Gewinnen kann nur, wer da ist: Jeder gezogene Name hat eine Frist; meldet
 * sich niemand, bis sie abläuft, zieht K4ia für diesen Preis neu. Wer am
 * Handy abgehakt wird („Gewinner ist da“), hat gewonnen.
 */

/** Zeit, die ein gezogener Gewinner hat, sich zu melden. */
export const GEWINNER_FRIST_MS = 10 * 60_000

export const GEWINNSPIEL_PHASEN = ['wartet', 'laeuft', 'fertig'] as const
export type GewinnspielPhase = (typeof GEWINNSPIEL_PHASEN)[number]

/** Instagram-Name, wie er gespeichert und verglichen wird: ohne @, klein, ohne Leerraum. */
export function handleNormalisieren(roh: string): string {
  return roh
    .trim()
    .replace(/^@+/, '')
    .replace(/^https?:\/\/(www\.)?instagram\.com\//i, '')
    .replace(/[/?#].*$/, '')
    .trim()
    .toLowerCase()
}

/** Instagram erlaubt Buchstaben, Ziffern, Punkt und Unterstrich, bis 30 Zeichen. */
export function istGueltigesHandle(handle: string): boolean {
  return /^[a-z0-9._]{1,30}$/.test(handle)
}

/** Höchstens so viele Gewinner je Preis. */
export const MAX_GEWINNER_JE_PREIS = 5

export const rafflePrizeSchema = z.object({
  id: z.string(),
  company: z.string(),
  prize: z.string(),
  logoKey: z.string().nullable(),
  sortOrder: z.number().int(),
  /** Wie viele Gewinner dieser Preis hat — aus derselben Liste, nie dieselbe Person. */
  winnersCount: z.number().int().min(1).max(MAX_GEWINNER_JE_PREIS),
  /** Zahl der Teilnehmer in der Liste dieses Preises. */
  participantCount: z.number().int().nonnegative(),
  /** Die bestätigten Gewinner, bei mehreren mit Komma getrennt. */
  winnerHandle: z.string().nullable(),
  winnerAt: z.string().nullable(),
})
export type RafflePrize = z.infer<typeof rafflePrizeSchema>

export const rafflePrizeInputSchema = z.object({
  company: z.string().trim().min(1).max(120),
  prize: z.string().trim().max(200).default(''),
  winnersCount: z.number().int().min(1).max(MAX_GEWINNER_JE_PREIS).default(1),
})
export type RafflePrizeInput = z.infer<typeof rafflePrizeInputSchema>

export const raffleParticipantsInputSchema = z.object({
  /** Roh, wie aus der Datei gelesen; der Worker normalisiert und entfernt Doppelte. */
  handles: z.array(z.string().max(200)).max(20_000),
})

/** Die Kennung eines Loses: Preis und Nummer des Gewinners (0 = erster). */
export function losId(preisId: string, los: number): string {
  return `${preisId}#${los}`
}

/**
 * Ein Los im laufenden Stand der Ziehung — je Preis so viele, wie er
 * Gewinner hat. Jedes Los hat einen eigenen Namen, eine eigene Frist und
 * ein eigenes Häkchen.
 */
export interface GewinnspielPreisStand {
  /** `losId(preisId, los)` */
  id: string
  preisId: string
  /** Nummer des Gewinners bei diesem Preis, ab 0. */
  los: number
  /** Der gezogene Name; `null`, wenn niemand mehr übrig ist. */
  gewinner: string | null
  /** Wann der aktuelle Name gezogen wurde (ms seit Epoche). */
  gezogenAm: number
  /** Bis wann er sich melden muss — gezogenAm + Frist. */
  fristBis: number
  /** Am Handy abgehakt: Dieser Name hat gewonnen. */
  bestaetigtAm: number | null
  /** Wie oft für diesen Preis schon gezogen wurde (1 = erste Ziehung). */
  ziehung: number
}

export interface GewinnspielZustand {
  phase: GewinnspielPhase
  gestartetAm: number
  /** Steigt mit jeder Ziehung (Start und jede Neuziehung); der Bildschirm animiert, wenn sie steigt. */
  runde: number
  /** Die Lose, in der Reihenfolge der Preise. */
  preise: GewinnspielPreisStand[]
  /** Zahl der Teilnehmer je Preis, aus denen gezogen wird. */
  teilnehmer: Record<string, number>
  /** Namen, die gezogen wurden und sich nicht gemeldet haben — nicht da, kommen nicht mehr dran. */
  nichtDa: string[]
  /** Was der Bildschirm zuletzt gemeldet hat. */
  bildschirm: { phase: 'wartet' | 'ziehung' | 'fertig'; gemeldetAm: number }
}

/** Ein Befehl vom Handy. */
export const gewinnspielBefehlSchema = z.discriminatedUnion('art', [
  z.object({ art: z.literal('start') }),
  z.object({ art: z.literal('bestaetigen'), losId: z.string().min(1).max(120) }),
  z.object({ art: z.literal('neu'), losId: z.string().min(1).max(120) }),
  z.object({ art: z.literal('reset') }),
])
export type GewinnspielBefehl = z.infer<typeof gewinnspielBefehlSchema>

/** mm:ss der verbleibenden Frist, nie unter 00:00. */
export function restzeit(fristBis: number, now: number): string {
  const rest = Math.max(0, Math.ceil((fristBis - now) / 1000))
  const m = Math.floor(rest / 60)
  const s = rest % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}
